const { request } = require('.')
// default export undefined on backend / tests
const Vue = require('vue').default || require('vue')

const TIMETRAVEL_LIMIT = 5 * 60 * 1000

/**
 * Takes `state` and `query` and returns a function to pass
 * `variables` to. This second function fetches the relevant data
 * and assigns different keys to the `state` depending on the
 * success or failure of the request.
 *
 * @param {Object} state - Vue Observable or plain object
 * @param {String} query - GraphQL query / mutation
 * @param {Object} variables - the variables relevant to the query
 * @returns {Object} `state`
 */
const fetchData = (state, query) => async variables => {
  const begin = new Date()
  const requestId = window.crypto.getRandomValues(new Uint32Array(1))[0].toString(16)
  const controller = new AbortController()
  const { signal } = controller
  Vue.set(state.pending, requestId, { query, variables, controller })

  try {
    const { data, errors } = await request(query, variables, { signal })
    state.complete[requestId] = { data, errors }
  } catch (error) {
    const aborted = error.name === 'AbortError'
    Vue.set(state.complete, requestId, {
      data: null,
      ...(!aborted && { errors: [error] }),
      aborted
    })
  } finally {
    Vue.set(state.complete[requestId], 'debug', { begin, end: new Date() })
    Vue.delete(state.pending, requestId)
  }

  // Delete old `completed` requests so we use memory responsibly for long sessions.
  // Generally, the `pending` requests are more valuable to actual end users anyway.
  Object.keys(state.complete).forEach(id => {
    if (begin - state.complete[id].debug.end > TIMETRAVEL_LIMIT) {
      Vue.delete(state.complete, id)
    }
  })

  return state.complete[requestId]
}

/**
 * Takes a GraphQL query / mutation and returns
 * an observable `state` object with a `fetch`
 * function to pass variables to
 *
 * @param {String} query - GraphQL query / mutation
 * @returns {Object} `state` - observable object representing request status
 */
function withQuery (query) {
  const state = Vue.observable({
    pending: {},
    complete: {},
    get isPending () {
      const pending = Object.values(this.pending)
      return cb => cb
        ? pending.some(({ variables }) => cb(variables))
        : Boolean(pending.length)
    },

    abort (cb) {
      const pending = Object.values(this.pending)
      pending.filter(({ variables }) => cb ? cb(variables) : true)
        .forEach(({ controller }) => controller.abort())
    }
  })
  state.fetch = fetchData(state, query)
  return state
}

module.exports = {
  fetchData,
  withQuery
}
